import EventType from '@src/model/agentChatEventType';
import { chatHistoryActions } from '@src/reducers/chatHistory';
import { contactPhoneLinked } from '@src/reducers/contactRToolkit';
import { chatByIdSelector } from '@src/selectors/chats';
import type { RootState } from '@src/store/reduxstore';
import type { Dispatch } from 'redux';

type ContactPhoneLinkedPayload = {
  id: string;
  phone: string;
  contactName?: string;
  timestamp: number;
};

type ContactPhoneLinkedEvent = {
  type: string;
  payload: ContactPhoneLinkedPayload;
};

type StoreLike = { dispatch: Dispatch; getState: () => RootState };

/**
 * Handles a live `CONTACT_PHONE_LINKED` agent-chat event (ENG-1304): the contact shared their
 * phone through a REQUEST_CONTACT_INFO request and it was linked.
 *
 * - Adds the inline item to the chat timeline, when that timeline is loaded. `messageId` is the
 *   payload id, so a redelivered push or the on-open history fetch does not duplicate it.
 * - Hands the phone to the contact panel, which switches to the linked state without a refetch.
 *
 * Runs whether or not this console window is the active one: both updates only touch state that
 * is rendered when the chat is open.
 */
const handleContactPhoneLinked = (
  store: StoreLike,
  chatId: string,
  { payload }: ContactPhoneLinkedEvent
): void => {
  store.dispatch(
    chatHistoryActions.unshiftEvent({
      referenceChatId: chatId,
      chatId,
      event: {
        type: EventType.CONTACT_PHONE_LINKED.id as 'CONTACT_PHONE_LINKED',
        messageId: payload.id,
        phone: payload.phone,
        contactName: payload.contactName,
        timestamp: payload.timestamp,
      },
    })
  );

  const contactId = chatByIdSelector(chatId)(store.getState())?.contact?.id;
  if (contactId) {
    store.dispatch(
      contactPhoneLinked({
        contactId: Number(contactId),
        mobileNumber: payload.phone,
      })
    );
  }
};

export default handleContactPhoneLinked;
